import { ChevronRight, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { AddPropertyForm, type NewProperty } from './add-property-form';
import { CREATED_BY_KEY } from './frontmatter-yaml';
import { PropertyRow } from './property-row';
import {
  effectiveType,
  emptyValueFor,
  formatTimestamp,
} from './property-values';
import { useNoteProperties } from './use-note-properties';
import { usePropertiesOpen } from './use-properties-open';

export interface PropertiesPanelProps {
  wsPath: string;
  wsName: string;
  canEdit: boolean;
}

function propertiesBodyId(wsPath: string): string {
  return `note-properties-${wsPath.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

export function PropertiesPanel({
  wsPath,
  wsName,
  canEdit,
}: PropertiesPanelProps) {
  const [open, setOpen] = usePropertiesOpen(wsName);
  const {
    properties,
    types,
    customKeys,
    createdBy,
    fileTimes,
    parseError,
    commitProperties,
  } = useNoteProperties(wsPath);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<NewProperty | null>(null);

  const count = customKeys.length;
  const label = t.app.properties.title({ n: count });
  const bodyId = propertiesBodyId(wsPath);

  const rowKeys =
    draft && !customKeys.includes(draft.key)
      ? [...customKeys, draft.key]
      : customKeys;

  function handleCommit(key: string, value: unknown) {
    const pending = draft && draft.key === key ? draft : null;
    const next = { ...properties, [key]: value };
    const nextTypes = { ...types };
    if (pending) {
      nextTypes[key] = pending.type;
      setDraft(null);
    }
    commitProperties(next, nextTypes);
  }

  function handleRemove(key: string) {
    const next = { ...properties };
    delete next[key];
    const nextTypes = { ...types };
    delete nextTypes[key];
    commitProperties(next, nextTypes);
  }

  function handleAdd(property: NewProperty) {
    setAdding(false);
    setDraft(property);
  }

  return (
    <div className="shrink-0" data-testid="note-properties">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((previous) => !previous)}
        className="flex min-w-0 max-w-full items-center gap-1 rounded py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          size={13}
          className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
        <span className="truncate">{label}</span>
      </button>

      {open && (
        <section
          id={bodyId}
          aria-label={t.app.properties.label}
          className="max-h-[45vh] overflow-y-auto pb-2"
        >
          {parseError && (
            <p role="alert" className="mb-2 px-1.5 text-sm text-amber-700 dark:text-amber-300">
              {t.app.properties.parseError}
            </p>
          )}

          <PropertyRow
            name={t.app.properties.created}
            value={formatTimestamp(fileTimes.ctime)}
            type="text"
            canEdit={false}
            system
            onCommit={() => undefined}
            onRemove={() => undefined}
            onCancelDraft={() => undefined}
          />
          <PropertyRow
            name={t.app.properties.createdBy}
            value={createdBy ?? '—'}
            type="text"
            canEdit={false}
            system
            onCommit={() => undefined}
            onRemove={() => undefined}
            onCancelDraft={() => undefined}
          />
          <PropertyRow
            name={t.app.properties.lastEdited}
            value={formatTimestamp(fileTimes.mtime)}
            type="text"
            canEdit={false}
            system
            onCommit={() => undefined}
            onRemove={() => undefined}
            onCancelDraft={() => undefined}
          />

          {rowKeys.map((key) => (
            <PropertyRow
              key={key}
              name={key}
              value={
                key in properties
                  ? properties[key]
                  : emptyValueFor(draft?.type ?? 'text')
              }
              type={
                key in properties
                  ? effectiveType(key, properties[key], types)
                  : (draft?.type ?? 'text')
              }
              canEdit={canEdit && !parseError}
              draft={!(key in properties)}
              onCommit={handleCommit}
              onRemove={handleRemove}
              onCancelDraft={() => setDraft(null)}
            />
          ))}

          {customKeys.length === 0 && !draft && (
            <p className="px-1.5 py-1 text-sm text-muted-foreground">
              {t.app.properties.empty}
            </p>
          )}

          {canEdit &&
            !parseError &&
            (adding ? (
              <AddPropertyForm
                existingKeys={[
                  ...Object.keys(properties),
                  CREATED_BY_KEY,
                ]}
                onAdd={handleAdd}
                onClose={() => setAdding(false)}
              />
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="mt-1 flex items-center gap-1.5 rounded px-1.5 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Plus size={13} aria-hidden="true" />
                {t.app.properties.add}
              </button>
            ))}
        </section>
      )}
    </div>
  );
}
